import React, { useState, useEffect } from 'react';
import useApi from '../hooks/useApi';

export default function UploadThemePage({ updateShowLogin, onShowSignUp }) {
  const [themeName, setThemeName] = useState('');
  const [themeImage, setThemeImage] = useState(null);
  const [hasAudio, setHasAudio] = useState(0);
  const [audioFile, setAudioFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [message, setMessage] = useState('');
  const { request } = useApi();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const premiumStatus = localStorage.getItem('is_premium');
    setIsPremium(premiumStatus === '1');
    setIsLoggedIn(!!token);
  }, []);

  const handleUpload = async () => {
    if (!themeName || !themeImage || (hasAudio === 1 && !audioFile)) {
      setMessage('Please fill in all required fields.');
      setStatus('error');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('title', themeName);
      formData.append('image_file', themeImage);
      formData.append('has_audio', hasAudio);
      if (hasAudio === 1 && audioFile) {
        formData.append('audio_file', audioFile);
      }

      const response = await request({
        method: 'post',
        url: 'api/v1/pdf_parser/user_themes/',
        data: formData,
        withAuth: true,
      });

      if (response.message) {
        setMessage(response.message);
        setThemeName('');
        setThemeImage(null);
        setAudioFile(null);
        setHasAudio(0);
        setStatus('success');
      }
    } catch (err) {
      console.error('Theme upload failed:', err);
      setMessage('Error occurred during theme upload.');
      setStatus('error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-10 p-6 border rounded shadow bg-white">
      {isLoggedIn ? (
        isPremium ? (
        <>
          <h2 className="text-2xl font-bold mb-4 text-center">Upload a Theme</h2>

          <input
            type="text"
            placeholder="Enter theme name"
            value={themeName}
            onChange={(e) => {
              setThemeName(e.target.value);
              setMessage('');
            }
            }
            className="mb-4 block w-full border rounded px-3 py-2"
          />

          <label className="block font-medium mb-1">Theme Image File</label>
          <input
            type="file"
            accept="image/*"
            id="theme-image-input"
            onChange={(e) => {
              setThemeImage(e.target.files[0]);
              setMessage('');
            }}
            className="mb-4 block w-full"
          />

          <label className="block font-medium mb-1">Does this theme have audio?</label>
          <div className="flex gap-6 mb-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="hasAudio"
                value="yes"
                checked={hasAudio === 1}
                onChange={() => {
                  setHasAudio(1);
                  setMessage('');
                }}
              />
              Yes
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="hasAudio"
                value="no"
                checked={hasAudio === 0}
                onChange={() => {
                  setHasAudio(0);
                  setMessage('');
                }}
              />
              No
            </label>
          </div>

          {hasAudio === 1&& (
            <>
              <label className="block font-medium mb-1">Upload Audio File</label>
              <input
                type="file"
                accept="audio/*"
                id="audio-file-input"
                onChange={(e) => {
                  setAudioFile(e.target.files[0]);
                  setMessage('');
                }}
                className="mb-4 block w-full"
              />
            </>
          )}

          <button
            onClick={handleUpload}
            disabled={uploading}
            className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 disabled:opacity-50 w-full"
          >
            {uploading ? 'Uploading...' : 'Upload Theme'}
          </button>
          {message && (
            <p
              className={`mt-4 text-center text-sm ${
                status === 'success'
                  ? 'text-green-600'
                  : status === 'error'
                  ? 'text-red-700'
                  : 'text-gray-600'
              }`}
            >
              {message}
            </p>
          )}
        </>
          ) : (
        <div className="text-center space-y-6 py-16">
          <h2 className="text-3xl font-bold text-gray-800">Upload your own themes!</h2>
          <p className="text-gray-600 text-lg">
            Theme upload is available for premium users only. Upgrade now to unlock custom themes!
          </p>
          <button
            onClick={() => window.location.href = '/pricing'}
            className="bg-yellow-600 text-white px-6 py-2 rounded hover:bg-yellow-700 transition"
          >
            Go Premium
          </button>
        </div>
      )
      ) : (
        <div className="text-center space-y-6 py-16">
          <h2 className="text-3xl font-bold text-gray-800">Please log in to upload themes</h2>
          <p className="text-gray-600 text-lg">Don't have an account? You can create one in seconds.</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={updateShowLogin}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
            >
              Log In
            </button>
            <button
              onClick={onShowSignUp}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
            >
              Sign Up
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
